import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  AlarmConfigParam,
  bodyToConfig,
  CommonOption,
  ERROR_CONTEXT_ADMINISTRATION,
  ERROR_CONTEXT_HEADER,
  Filter,
  GetCentersParam,
  GetDistributorDefaultResponse,
  GetLinesParam,
  GetTrafosParam,
  RegularOption,
  UpdateDataDistributorConfigParam,
} from '@data';
import {
  DISABLE_SAVED_CONFIG_ENDPOINT,
  ENABLE_SAVED_CONFIG_ENDPOINT,
  GET_CENTERS_ENDPOINT,
  GET_CONFIG_CENTER_ENDPOINT,
  GET_CONFIG_CENTER_FILTER_ENDPOINT,
  GET_DATA_CONFIG_DISTRIBUTOR_ENDPOINT,
  GET_LINES_CENTERS_ENDPOINT,
  GET_TRAFOS_ENDPOINT,
} from '../constants/endpoints.constants';
import { HttpParams } from '@angular/common/http';
import { withSpinner } from '../../app/core/services/spinner.controller';

@Injectable({ providedIn: 'root' })
export class DataAdministrationService {
  protected readonly apiService = inject(ApiService);

  /**
   * Retrieves the list of centers from the API.
   * @param body - Optional parameters for the request.
   * @returns An Observable emitting an array of CommonOption.
   */
  getCenters(body?: GetCentersParam) {
    return this.apiService
      .post<CommonOption[]>(GET_CENTERS_ENDPOINT, body)
      .pipe(withSpinner());
  }

  /**
   * Retrieves the list of transformers (trafos) from the API.
   * @param body - Optional parameters for the request.
   * @returns An Observable emitting an array of CommonOption.
   */
  getTrafos(body?: GetTrafosParam) {
    return this.apiService
      .post<CommonOption[]>(GET_TRAFOS_ENDPOINT, body)
      .pipe(withSpinner());
  }

  /**
   * Retrieves the list of lines from the API.
   * @param body - Optional parameters for the request.
   * @returns An Observable emitting an array of RegularOption.
   */
  getLines(body?: GetLinesParam) {
    return this.apiService
      .post<RegularOption[]>(GET_LINES_CENTERS_ENDPOINT, body)
      .pipe(withSpinner());
  }

  /**
   * Retrieves the configuration for a specific center.
   * @param params - The center ID or parameters for the request.
   * @returns An Observable emitting the distributor default response.
   */
  getCenterConfig(params?: string) {
    return this.apiService
      .get<GetDistributorDefaultResponse>(
        `${GET_CONFIG_CENTER_FILTER_ENDPOINT}/${params}`,
      )
      .pipe(withSpinner());
  }

  /**
   * Updates the saved configuration for a specific center.
   * @param body - The configuration data to update.
   * @param centerSaveId - The ID of the center to update.
   * @returns An Observable emitting the updated distributor default response.
   */
  updateSavedCenterConfig(body?: bodyToConfig, centerSaveId?: string) {
    return this.apiService
      .patch<GetDistributorDefaultResponse>(
        `${GET_CONFIG_CENTER_FILTER_ENDPOINT}/${centerSaveId}`,
        body,
        {
          headers: {
            [ERROR_CONTEXT_HEADER]: ERROR_CONTEXT_ADMINISTRATION,
          },
        },
      )
      .pipe(withSpinner());
  }

  /**
   * Saves the configuration for a center.
   * @param body - The configuration data to save.
   * @returns An Observable emitting the distributor default response.
   */
  saveCenterConfig(body?: string) {
    return this.apiService
      .post<GetDistributorDefaultResponse>(
        GET_CONFIG_CENTER_FILTER_ENDPOINT,
        body,
        {
          headers: {
            [ERROR_CONTEXT_HEADER]: ERROR_CONTEXT_ADMINISTRATION,
          },
        },
      )
      .pipe(withSpinner());
  }

  /**
   * Retrieves all center configurations with optional filters and pagination.
   * @param params - Parameters including deviceType, centerId, filters, pageIndex, and pageSize.
   * @returns An Observable emitting the center configuration data.
   */
  getAllCenterConfig(params?: any) {
    const { deviceType, centerId, filters, pageIndex, pageSize } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    if (deviceType !== null && deviceType !== undefined)
      queryObj['deviceType'] = deviceType;
    if (pageSize !== null && pageSize !== undefined)
      queryObj['pageSize'] = pageSize;
    if (pageIndex !== null && pageIndex !== undefined)
      queryObj['pageIndex'] = pageIndex;
    const queryParams = new HttpParams().appendAll(queryObj);
    const body: any = this.getFiltersBody(centerId, filters);
    return this.apiService
      .post<any>(GET_CONFIG_CENTER_ENDPOINT, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
  }

  /**
   * Retrieves the distributor configuration data from the API.
   * @param params - Parameters for the request.
   * @returns An Observable emitting the distributor configuration data.
   */
  getDataDistributorConfig(params?: any) {
    params = { ...params };
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<any>(`${GET_DATA_CONFIG_DISTRIBUTOR_ENDPOINT}?${queryParams}`)
      .pipe(withSpinner());
  }

  /**
   * Updates the distributor configuration data.
   * @param params - Parameters including deviceType, distributorIds, and body.
   * @returns An Observable emitting the updated distributor configuration data.
   */
  updateDataDistributorConfig(params: UpdateDataDistributorConfigParam) {
    const { deviceType, distributorIds } = params;
    const { body } = params;
    const queryParams = new HttpParams().appendAll({
      deviceType,
      distributorIds,
    });
    return this.apiService
      .patch<any>(
        `${GET_DATA_CONFIG_DISTRIBUTOR_ENDPOINT}?${queryParams}`,
        body,
      )
      .pipe(withSpinner());
  }

  /**
   * Adds a new distributor configuration.
   * @param params - Parameters including the configuration body.
   * @returns An Observable emitting the result of the addition.
   */
  addDataDistributorConfig(params: UpdateDataDistributorConfigParam) {
    const { body } = params;
    return this.apiService
      .post<any>(GET_DATA_CONFIG_DISTRIBUTOR_ENDPOINT, body)
      .pipe(withSpinner());
  }

  /**
   * Enables a saved configuration.
   * @param body - The configuration parameters to enable.
   * @returns An Observable emitting the result of the operation.
   */
  enableConfig(body: AlarmConfigParam) {
    return this.apiService
      .patch<any>(ENABLE_SAVED_CONFIG_ENDPOINT, body)
      .pipe(withSpinner());
  }

  /**
   * Disables a saved configuration.
   * @param body - The configuration parameters to disable.
   * @returns An Observable emitting the result of the operation.
   */
  disableConfig(body: AlarmConfigParam) {
    return this.apiService
      .patch<any>(DISABLE_SAVED_CONFIG_ENDPOINT, body)
      .pipe(withSpinner());
  }

  /**
   * Deletes a saved configuration.
   * @param body - The configuration parameters to delete.
   * @returns An Observable emitting the result of the deletion.
   */
  deleteConfig(body: AlarmConfigParam) {
    return this.apiService
      .deleteWithHeaders<any>(GET_CONFIG_CENTER_FILTER_ENDPOINT, body, {
        [ERROR_CONTEXT_HEADER]: ERROR_CONTEXT_ADMINISTRATION,
      })
      .pipe(withSpinner());
  }

  /**
   * Constructs the filters body for API requests.
   * @param query - The query string or center ID.
   * @param filters - Array of filter objects.
   * @returns The constructed body object for the API request.
   */
  private getFiltersBody(
    query: string | undefined,
    filters: Filter[] | undefined,
  ) {
    if (filters !== null && filters !== undefined) {
      let bodyFilters = filters!.map((filter) => ({
        ...filter,
        operator: filter?.operator || 'equals',
      }));
      bodyFilters = { ...bodyFilters };
      const body: any = { filters: bodyFilters, query };
      return body;
    }
  }
}
